import type { EventStatus, EventVisibility, InstanceStatus, RecurringSeries } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'

/**
 * Events, their dates, and the rooms booked for them.
 *
 * Screens read the **calendar** — instances in a window — rather than events,
 * because that is what a month grid renders and what a booking or a
 * registration attaches to. The event is the rule; the instance is what
 * happens.
 */

export interface CalendarWindow {
  from?: string
  to?: string
  campusId?: string
  category?: string
  /** Comma-separated tag ids: any within a group, every group picked. */
  tags?: string
  featured?: boolean
}

export const eventKeys = {
  all: () => ['events'] as const,
  list: (filters: Record<string, unknown> = {}) => [...eventKeys.all(), 'list', filters] as const,
  detail: (id: string) => [...eventKeys.all(), 'detail', id] as const,
  instances: (id: string) => [...eventKeys.all(), 'instances', id] as const,
  calendar: (window: CalendarWindow = {}) => [...eventKeys.all(), 'calendar', window] as const,
  feeds: () => [...eventKeys.all(), 'feeds'] as const,
  tags: () => [...eventKeys.all(), 'tags'] as const,
  files: (eventId: string) => [...eventKeys.all(), 'files', eventId] as const,
}

export function useCalendar(window: CalendarWindow) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.calendar(window),
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.calendar.get({ query: window })
      if (error) throw error
      return data
    },
  })
}

export type CalendarEntry = NonNullable<ReturnType<typeof useCalendar>['data']>[number]

export function useEvents(filters: { status?: EventStatus; campusId?: string } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.list(filters),
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.get({ query: filters })
      if (error) throw error
      return data
    },
  })
}

export type ChurchEvent = NonNullable<ReturnType<typeof useEvents>['data']>[number]

export function useEvent(eventId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.detail(eventId),
    enabled: enabled && eventId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).get()
      if (error) throw error
      return data
    },
  })
}

/**
 * One date by its id.
 *
 * Not a filtered `useCalendar`: that window is clamped to 400 days from its
 * *start*, so asking for "a year either side of today" quietly returns only the
 * past year — which is how a date three days away came back as "not found".
 */
export function useEventInstance(instanceId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: [...eventKeys.all(), 'instance', instanceId] as const,
    enabled: enabled && instanceId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.instances({ id: instanceId }).get()
      if (error) throw error
      return data
    },
  })
}

export function useEventInstances(eventId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.instances(eventId),
    enabled: enabled && eventId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).instances.get()
      if (error) throw error
      return data
    },
  })
}

function useEventInvalidation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: eventKeys.all() })
}

export interface EventInput {
  title: string
  description?: string | null
  location?: string | null
  category?: string | null
  campusId?: string | null
  visibility?: EventVisibility
  status?: EventStatus
  schedule?: RecurringSeries | null
  startsAt?: string
  endsAt?: string
  allDay?: boolean
  featured?: boolean
  linkUrl?: string | null
  linkLabel?: string | null
  tagIds?: string[]
}

export function useCreateEvent() {
  const api = useApi()
  const invalidate = useEventInvalidation()
  return useMutation({
    mutationFn: async (body: EventInput) => {
      const { data, error } = await api.api.v1.events.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

/**
 * Updating a series returns `kept`: how many edited dates the change left
 * alone. Screens say so out loud, because otherwise somebody has to go and
 * check whether their edits survived.
 */
export function useUpdateEvent(eventId: string) {
  const api = useApi()
  const invalidate = useEventInvalidation()
  return useMutation({
    mutationFn: async (body: Partial<Omit<EventInput, 'startsAt' | 'endsAt'>>) => {
      const { data, error } = await api.api.v1.events({ id: eventId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateInstance() {
  const api = useApi()
  const invalidate = useEventInvalidation()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      title?: string | null
      description?: string | null
      location?: string | null
      status?: InstanceStatus
      startsAt?: string
      endsAt?: string
    }) => {
      const { data, error } = await api.api.v1.events.instances({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteEvent() {
  const api = useApi()
  const invalidate = useEventInvalidation()
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await api.api.v1.events({ id: eventId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
}

// ---- subscription links ----------------------------------------------------

export function useCalendarFeeds(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.feeds(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.feeds.get()
      if (error) throw error
      return data
    },
  })
}

export type CalendarFeed = NonNullable<ReturnType<typeof useCalendarFeeds>['data']>[number]

export function useFeedMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: eventKeys.feeds() })

  const create = useMutation({
    mutationFn: async (body: {
      name: string
      campusId?: string | null
      category?: string | null
      includeMembersOnly?: boolean
    }) => {
      const { data, error } = await api.api.v1.events.feeds.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })

  const revoke = useMutation({
    mutationFn: async (feedId: string) => {
      const { error } = await api.api.v1.events.feeds({ id: feedId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })

  return { create, revoke }
}
